import { makeAutoObservable, observable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { TProjectMember, TState } from "@/types/issue-metadata";
import type { TProjectMembership } from "@/types/project";
import type { IWorkspaceMember } from "@/types/workspace";

export interface TMetadataErrors {
  states: string | null;
  members: string | null;
  membership: string | null;
}

const METADATA_FRESHNESS_MS = 30_000;

const emptyErrors = (): TMetadataErrors => ({
  states: null,
  members: null,
  membership: null,
});

type TRawProjectMember = Record<string, unknown>;

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isProjectMemberShape = (value: unknown): value is TProjectMember =>
  isObjectRecord(value) && typeof value.id === "string" && typeof value.display_name === "string";

const isProjectMembershipShape = (value: unknown): value is TProjectMembership =>
  isObjectRecord(value) && typeof value.member === "string";

const isProjectMemberLiteShape = (
  value: unknown,
): value is {
  member_id: string;
  member__display_name?: string;
  member__avatar_url?: string | null;
} => isObjectRecord(value) && typeof value.member_id === "string";

const toProjectMember = (
  id: string,
  details?: {
    avatar_url?: string | null;
    display_name?: string | null;
    email?: string;
    first_name?: string;
    last_name?: string;
  },
): TProjectMember => ({
  id,
  first_name: details?.first_name ?? "",
  last_name: details?.last_name ?? "",
  email: details?.email ?? "",
  avatar: details?.avatar_url ?? "",
  avatar_url: details?.avatar_url ?? "",
  display_name: details?.display_name ?? id,
});

export class IssueMetadataStore {
  statesByProject = observable.map<string, TState[]>();
  statesByWorkspace = observable.map<string, TState[]>();
  membersByProject = observable.map<string, TProjectMember[]>();
  membershipByProject = observable.map<string, TProjectMembership | null>();
  loadingByProject = observable.map<string, boolean>();
  errorsByProject = observable.map<string, TMetadataErrors>();
  fetchedAtByProject = observable.map<string, number>();
  loadingByWorkspace = observable.map<string, boolean>();
  fetchedAtByWorkspace = observable.map<string, number>();

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  getStates(projectId: string): TState[] {
    return this.statesByProject.get(projectId) ?? [];
  }

  /**
   * Work-item states for the whole workspace, independent of any project.
   *
   * Statuses are workspace-scoped (`/work-item-states/`) — `statesByProject`
   * only caches the same list under a project key. Surfaces that need statuses
   * before a project is chosen (the create dialog's project-less event path,
   * #3754) read them from here instead of getting an empty list.
   */
  getWorkspaceStates(workspaceSlug: string): TState[] {
    return this.statesByWorkspace.get(workspaceSlug) ?? [];
  }

  /**
   * Load the workspace's work-item states unless a fresh copy is already
   * cached. Safe to call on every mount/open — repeat calls inside the
   * freshness window and concurrent calls are no-ops.
   */
  async ensureWorkspaceStates(workspaceSlug: string): Promise<void> {
    if (!workspaceSlug) return;
    if (this.loadingByWorkspace.get(workspaceSlug)) return;
    const lastFetch = this.fetchedAtByWorkspace.get(workspaceSlug) ?? 0;
    if (Date.now() - lastFetch < METADATA_FRESHNESS_MS) return;

    this.loadingByWorkspace.set(workspaceSlug, true);
    try {
      const states = await this.rootStore.workItemStateService.list(workspaceSlug);
      runInAction(() => {
        this.statesByWorkspace.set(workspaceSlug, states);
        this.fetchedAtByWorkspace.set(workspaceSlug, Date.now());
      });
    } catch (error) {
      // Recoverable: the caller falls back to whatever is already cached, so a
      // status picker degrades to empty rather than breaking the surface.
      console.warn("Failed to fetch workspace work-item states", { workspaceSlug, error });
    } finally {
      runInAction(() => {
        this.loadingByWorkspace.set(workspaceSlug, false);
      });
    }
  }

  getMembers(projectId: string): TProjectMember[] {
    return this.membersByProject.get(projectId) ?? [];
  }

  getErrors(projectId: string): TMetadataErrors {
    return this.errorsByProject.get(projectId) ?? emptyErrors();
  }

  private async normalizeProjectMembers(
    workspaceSlug: string,
    rawMembers: unknown[],
  ): Promise<TProjectMember[]> {
    if (rawMembers.length === 0) return [];

    if (rawMembers.every(isProjectMemberShape)) {
      return rawMembers;
    }

    if (rawMembers.every(isProjectMemberLiteShape)) {
      return rawMembers.map((member) =>
        toProjectMember(member.member_id, {
          avatar_url: member.member__avatar_url,
          display_name: member.member__display_name ?? member.member_id,
        }),
      );
    }

    if (rawMembers.every(isProjectMembershipShape)) {
      // The endpoint already returns per-member `member_detail` (name + email +
      // avatar) for every project member. Only fall back to the workspace-member
      // map / current user for memberships that lack it — and skip that fetch
      // entirely when every membership carries its own detail. Without this, a
      // project member absent from the workspace map fell through to
      // `toProjectMember(id)`, leaking the raw user UUID as the display name.
      const needsWorkspaceLookup = rawMembers.some((membership) => !membership.member_detail);
      let workspaceMembers: IWorkspaceMember[] = [];
      if (needsWorkspaceLookup) {
        try {
          workspaceMembers = await this.rootStore.workspaceMembers.readMembers(workspaceSlug);
        } catch (err) {
          console.warn(
            "[issue-metadata] workspace members fetch failed; using empty list",
            { workspaceSlug },
            err,
          );
          workspaceMembers = [];
        }
      }

      const workspaceMemberMap = new Map(
        workspaceMembers.map((member) => [member.member.id, member]),
      );
      const currentUser = this.rootStore.user.currentUser;

      return rawMembers.map((membership) => {
        if (membership.member_detail) {
          return toProjectMember(membership.member, membership.member_detail);
        }
        const workspaceMember = workspaceMemberMap.get(membership.member);
        if (workspaceMember) {
          return toProjectMember(membership.member, workspaceMember.member);
        }
        if (currentUser?.id === membership.member) {
          return toProjectMember(membership.member, currentUser);
        }
        return toProjectMember(membership.member);
      });
    }

    return rawMembers
      .filter(isObjectRecord)
      .map((member: TRawProjectMember) =>
        toProjectMember(
          typeof member.member_id === "string"
            ? member.member_id
            : typeof member.member === "string"
              ? member.member
              : typeof member.id === "string"
                ? member.id
                : "",
          {
            avatar_url:
              typeof member.member__avatar_url === "string"
                ? member.member__avatar_url
                : typeof member.avatar_url === "string"
                  ? member.avatar_url
                  : null,
            display_name:
              typeof member.member__display_name === "string"
                ? member.member__display_name
                : typeof member.display_name === "string"
                  ? member.display_name
                  : typeof member.member === "string"
                    ? member.member
                    : typeof member.id === "string"
                      ? member.id
                      : "",
            email: typeof member.email === "string" ? member.email : "",
            first_name: typeof member.first_name === "string" ? member.first_name : "",
            last_name: typeof member.last_name === "string" ? member.last_name : "",
          },
        ),
      )
      .filter((member) => member.id);
  }

  async fetchForProject(workspaceSlug: string, projectId: string): Promise<void> {
    if (this.loadingByProject.get(projectId)) return;
    const lastFetch = this.fetchedAtByProject.get(projectId) ?? 0;
    if (Date.now() - lastFetch < METADATA_FRESHNESS_MS) return;

    this.loadingByProject.set(projectId, true);
    const errors = emptyErrors();

    // Work-item states live behind the workspace-scoped /work-item-states/
    // endpoint, which a project viewer (no workspace-level read) 403s on. They
    // are also embedded on the project detail payload, so on a failed fetch fall
    // back to the embedded copy — a viewer still gets states (Board columns /
    // status cells render) instead of a blank board.
    const embeddedStates = (): TState[] => {
      const proj = this.rootStore.project.getProjectById(workspaceSlug, projectId);
      return (proj && "work_item_states" in proj ? proj.work_item_states : undefined) ?? [];
    };
    const statesP = this.rootStore.workItemStateService
      .list(workspaceSlug)
      .then((states) =>
        runInAction(() => {
          this.statesByProject.set(projectId, states);
          this.statesByWorkspace.set(workspaceSlug, states);
          // Same authoritative workspace-scoped list ``ensureWorkspaceStates``
          // fetches — stamp it so that call is a no-op instead of a duplicate
          // request. Not stamped on the embedded-states fallback below, which is
          // a project-scoped copy.
          this.fetchedAtByWorkspace.set(workspaceSlug, Date.now());
        }),
      )
      .catch(() => {
        const fallback = embeddedStates();
        if (fallback.length) {
          runInAction(() => {
            this.statesByProject.set(projectId, fallback);
            this.statesByWorkspace.set(workspaceSlug, fallback);
          });
        } else {
          errors.states = "Failed to fetch states";
        }
      });

    // The roster and the user's own membership are read through the project
    // members store, the one owner of both (R9, R10): with `query-cache` on, a
    // read overlapping the members page's shares its request.
    const membersP = this.rootStore.projectMembers
      .readMembers(workspaceSlug, projectId)
      .then((raw) => this.normalizeProjectMembers(workspaceSlug, raw))
      .then((members) =>
        runInAction(() => {
          this.membersByProject.set(projectId, members);
        }),
      )
      .catch((error: unknown) => {
        console.warn("[IssueMetadataStore] project members fetch failed", {
          workspaceSlug,
          projectId,
          error,
        });
        errors.members = "Failed to fetch members";
      });

    // memberMe: fire-and-forget background call — result is stored but not
    // awaited. Nothing in the issues page reads membershipByProject; it is
    // only used by the project-settings members panel.
    this.rootStore.projectMembers
      .readMemberMe(workspaceSlug, projectId)
      .then((membership) =>
        runInAction(() => {
          this.membershipByProject.set(projectId, membership);
        }),
      )
      .catch((error: unknown) => {
        // Non-fatal: the members panel renders without the viewer's own row.
        console.warn("[IssueMetadataStore] own project membership fetch failed", {
          workspaceSlug,
          projectId,
          error,
        });
      });

    await Promise.all([statesP, membersP]);

    runInAction(() => {
      this.errorsByProject.set(projectId, errors);
      this.loadingByProject.set(projectId, false);
      this.fetchedAtByProject.set(projectId, Date.now());
    });
  }
}
